import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Loader2, Megaphone, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field, StateSelect } from "@/components/FormBits";

export default function Advertise() {
  const nav = useNavigate();
  const [state, setState] = useState("");
  const [weeks, setWeeks] = useState(1);
  const [f, setF] = useState({ business_name: "", contact_name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!state) return toast.error("Pick the state for your ad");
    setBusy(true);
    try {
      const { data } = await api.post("/sponsors/request", { ...f, state, weeks });
      nav(`/advertise/confirmation/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <div className="page max-w-3xl">
      <p className="eyebrow">Sponsors & advertisers</p>
      <h1 className="font-heading mt-2 flex items-center gap-3 text-5xl text-white sm:text-6xl"><Megaphone className="text-[#fde68a]" /> Advertise With Us</h1>
      <p className="mt-3 text-zinc-400">Put your photo ad in front of people searching for happy hours in your state. $100 per week.</p>
      <form onSubmit={submit} className="mt-8 space-y-6">
        <section className="panel p-6">
          <p className="eyebrow mb-4">1 · Which state?</p>
          <StateSelect value={state} onChange={setState} testId="advertise-state-select" label="State (all 50 + DC)" />
        </section>
        <section className="panel p-6">
          <p className="eyebrow mb-4">2 · How many weeks?</p>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button type="button" data-testid="advertise-weeks-minus" onClick={() => setWeeks((w) => Math.max(1, w - 1))} disabled={weeks <= 1} className="flex h-11 w-11 items-center justify-center rounded-full border border-zinc-700 hover:border-[#fde68a] disabled:opacity-30"><Minus size={16} /></button>
              <span data-testid="advertise-weeks-value" className="w-24 text-center text-2xl font-extrabold text-white">{weeks} wk{weeks > 1 ? "s" : ""}</span>
              <button type="button" data-testid="advertise-weeks-plus" onClick={() => setWeeks((w) => Math.min(12, w + 1))} disabled={weeks >= 12} className="flex h-11 w-11 items-center justify-center rounded-full border border-zinc-700 hover:border-[#fde68a] disabled:opacity-30"><Plus size={16} /></button>
            </div>
            <p className="text-zinc-400">$100 × {weeks} = <span data-testid="advertise-total" className="font-heading text-4xl text-[#fde68a]">${weeks * 100}</span></p>
          </div>
        </section>
        <section className="panel p-6">
          <p className="eyebrow mb-4">3 · Contact info</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Business name" id="advertise-business-name" required value={f.business_name} onChange={set("business_name")} />
            <Field label="Your name" id="advertise-contact-name" required value={f.contact_name} onChange={set("contact_name")} />
            <Field label="Email" id="advertise-email" type="email" required value={f.email} onChange={set("email")} />
            <Field label="Phone number" id="advertise-phone" type="tel" required value={f.phone} onChange={set("phone")} />
          </div>
          <p data-testid="advertise-photo-note" className="mt-4 flex items-start gap-2 rounded-xl bg-[#fde68a]/10 p-3 text-sm text-[#fde68a]">
            <Camera size={16} className="mt-0.5 shrink-0" /> Include your name, email and phone number — we will contact you to get a photo for the ad.
          </p>
        </section>
        <button data-testid="advertise-submit-button" disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#fde68a] font-bold text-zinc-950 disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin" />} Continue · ${weeks * 100}
        </button>
      </form>
    </div>
  );
}
